<template>
  <div
    v-if="data"
    class="ProductForm"
    data-ht-module="pdp-product-form"
    :class="{
      'ProductForm--BuildStep': isBuildStep && !isQV,
      'ProductForm--ProductStep': isBuilding && !isQV,
      'ProductForm--ReviewStep': isReviewStep && !isQV,
    }"
  >
    <template v-if="isReviewStep && !isQV">
      <BundleReviewStepped />
      <PromotionalTile
        v-if="discountMessagingPermitted && store.promoReady"
        :tags="data.item.tags"
        :location="location"
      />
    </template>
    <template v-else>
      <p v-if="data.type == 'product'" class="p5 ProductForm__Step">
        Step {{ data.step }} of {{ totalProductSteps }}
      </p>
      <div class="ProductForm__Info">
        <h1 class="t2 ProductForm__Title">
          {{ productTitle }}
        </h1>
        <ReviewStars
          v-if="showReviews"
          :product="store.reviewsProduct"
          :location="location"
          :ygroup="yGroup"
        />
      </div>
      <p
        v-if="shortDescription"
        :class="[
          { 'ShortDesc--withQV': location == 'quick-view' },
          'ProductForm__ShortDesc',
        ]"
      >
        <span class="p4" v-html="shortDescription"></span>
      </p>
      <a
        v-if="location == 'quick-view'"
        :href="url"
        class="ProductForm__QVLink p4"
        data-color="night-sky-dark"
        >Get All The Details.
        <span class="link link--bold">View Full Page</span>
      </a>
      <label
        v-if="isDropship"
        class="t4 ProductForm__Dropship"
        data-type="modal"
        :for="`dropship-${data.item.handle}`"
      >
        {{ store.terms?.modal?.spaces || 'Spaces by Brooklinen' }}
        <Icon :icon="'tooltip'" />
      </label>
      <RelatedFabrics v-if="!isQV" :data="data" :terms="store.terms" />
      <OptionList
        v-for="list in lists"
        v-if="(!isBuildStep && !isReviewStep) || isQV"
        :key="list.name"
        :location="location"
        :is-auto-bundle="isAutoBundle"
        :is-gift-card="isGiftCard"
        :option="list"
        :data="data"
        :edit="edit"
        @update-product="updateProduct"
      />

      <Eyebrow :ready="store.promoReady" :item="data.item" :location="'pdp'" />

      <div class="ProductForm__PriceContainer">
        <Price
          :item="data.item"
          :variant="data.selected?.variant"
          :current-step="currentStep"
          :shell-component="shellComponent"
          :location="location"
          :edit="edit"
          :show-messaging="!isBuilding"
          :ready="store.promoReady"
          :selections="currentSelections"
          :promoted-option-groups="promotedOptionGroups"
          :selected-product="selectedProduct"
          :components="components"
        />
      </div>

      <BundleReview
        v-if="isAutoBundle"
        :item="data.item"
        :selections="currentSelections"
        :promoted-option-groups="promotedOptionGroups"
      />
      <Notification
        v-if="!isAutoBundle && location !== 'quick-view'"
        :free-shipping-title="store.freeShippingTerms?.banner_title"
        :free-shipping-copy="store.freeShippingTerms?.banner_copy"
      />
      <ProductActions
        :location="location"
        :edit="edit"
        :data="data"
        :is-auto-bundle="isAutoBundle"
        :is-gift-card="isGiftCard"
        :selected-product="selectedProduct"
        @close="close"
      />
      <Fomo v-if="fomoEnabled" class="ProductForm__Fomo" />
      <ProductUpgrade
        v-if="store.upgrade?.product"
        :text="store.upgrade.text"
        :title="store.upgrade.product.title"
        :url="store.upgrade.product.onlineStoreUrl.split('/products/')[1]"
        :icon-url="store.settings.upgrade_icon"
      />
      <PromotionalTile
        v-if="discountMessagingPermitted && store.promoReady"
        :tags="selectedProduct.tags"
        :location="location"
      />
      <Upsell
        v-if="store.upsell.loaded && eligibleLocation"
        v-show="hasUpsellItems"
        :items="store.upsell.items"
        :selection-listener="true"
        :pre-selection="store.selected.gallery"
        :title="store.terms.upsell.title"
        :location="location"
      />
      <Loyalty />
    </template>
  </div>
</template>

<script setup>
import { computed, ref, onMounted } from 'vue';
import ProductTabs from '@components/product-tabs';
import addOverlayListeners from '@utils/overlay';
import { CartWatcher } from '@utils/ProductResources';
import { useProductStore } from './Composables/Store';
import { initCustomAsset } from '@utils/global-functions';
import SizeGuide from '@components/size-guide';
import Fomo from './Fomo.vue';
import Icon from '../Global/Icon.vue';
import Price from '../Global/Price.vue';
import OptionList from './OptionList.vue';
import Upsell from '../Global/Upsell.vue';
import Eyebrow from '../Global/Eyebrow.vue';
import BundleReview from './BundleReview.vue';
import Notification from './Notification.vue';
import ProductActions from './ProductActions.vue';
import ReviewStars from '../Global/ReviewStars.vue';
import PromotionalTile from './PromotionalTile.vue';
import BundleReviewStepped from './BundleReview-Stepped.vue';
import ProductUpgrade from './ProductUpgrade.vue';
import Loyalty from '../Global/Loyalty.vue';
import RelatedFabrics from './RelatedFabrics.vue';

const store = useProductStore();
const components = computed(() => store.getSelectedComponents);
const emits = defineEmits(['select', 'handle-close']);
const props = defineProps({
  isAutoBundle: {
    type: Boolean,
    required: false,
    default: false,
  },
  item: {
    type: Object,
    required: false,
    default: false,
  },
  edit: {
    type: Boolean,
    required: false,
    default: false,
  },
  currentStep: {
    type: Number,
    required: false,
  },
  shellComponent: {
    type: Object,
    required: false,
  },

  location: {
    type: String,
    required: false,
  },
  variant: {
    type: [Object, String],
    required: false,
  },
  url: {
    type: String,
    required: false,
  },
});

const data = ref(false);
const lists = ref([]);
const isQV = computed(() => props.location == 'quick-view');

const productTitle = computed(() => {
  const item = data.value.item;
  const step =
    props?.item?.type && props?.item?.type == 'product'
      ? props?.item?.step
      : null;
  return step ? store?.$state?.steppedCopy[`step_${step}_title`] : item?.title;
});

// YGroup is a tag that orignates in Yotpo. It is used to group reviews together.
// Now that we're launching 2.0 versions of products, we're using the tag to
// show 1.0 and 2.0 product version reviews on the 2.0 PDPs.
const yGroup = computed(() => {
  const { tags } = data?.value?.item ?? [];
  const hasYGroup = tags.some((tag) => tag.indexOf('YGroup_') > -1);
  return hasYGroup;
});

const discountMessagingPermitted = computed(() => {
  const { tags } = data?.value?.item ?? [];
  const isPermitted = !tags.includes('Discount Messaging: Deny');
  return isPermitted;
});

const shortDescription = computed(() => {
  const item = data.value.item;
  const step =
    props?.item?.type && props?.item?.type == 'product'
      ? props?.item?.step
      : null;
  const description = step
    ? store?.$state?.steppedCopy[`step_${step}_description`]
    : item?.shortDescription?.value;
  return description ? initCustomAsset(description) : '';
});

const isDropship = computed(() => {
  const item = data.value.item;
  return item.tags.find((tag) => tag === 'Marketplace') ? true : false;
});

const isGiftCard = computed(() => {
  const item = data.value.item;
  return item.handle.includes('giftcard');
});

const hasUpsellItems = computed(() => {
  const items = store.upsell.items.filter((item) => item);
  return items.length > 0;
});

const eligibleLocation = computed(() => {
  return !isQV.value && props.location !== 'auto-bundle-modal';
});

const totalProductSteps = computed(
  () => store.steps?.filter((step) => step.type === 'product')?.length
);

const isBuildStep = computed(() => store.isBuildStep);
const isReviewStep = computed(() => store.isReviewStep);
const isBuilding = computed(() => store.isBuilding);
const fomoEnabled = computed(() => Boolean(store.settings.fomo));
const showReviews = computed(
  () =>
    !store.isBuilding &&
    store.reviewsEnabled &&
    props.location !== 'auto-bundle-modal'
);

const selectedProduct = computed(() => {
  const product = store.determineSelectedProduct
    ? store.determineSelectedProduct
    : props.item.item;

  return product;
});

const updateProduct = (payload) => emits('select', payload);

const currentSelections = computed(() => {
  const selections = props.item
    ? props.item.selected.options
    : store.$state.selected.options;
  if (selections.length <= 0) return;
  const values = selections.map((selection) => selection.value);
  return values;
});

const promotedOptionGroups = computed(() => {
  const context = data.value?.item ? data.value.item : store.$state.item;

  const groups =
    (context?.item
      ? context?.item.promotedOptionGroups
      : context.promotedOptionGroups) ?? [];
  const secondaryItems = data.value?.secondaryItems
    ? data.value.secondaryItems
    : store.$state?.secondaryItems ?? [];

  if (secondaryItems.length <= 0) {
    return groups;
  }

  const combinedGroups = secondaryItems
    .reduce((arr, item) => {
      if (item?.promotedOptionGroups) {
        if (item.promotedOptionGroups?.references?.nodes.length > 0)
          item.promotedOptionGroups =
            item.promotedOptionGroups.references.nodes;
        arr.push(item.promotedOptionGroups);
      }
      return arr;
    }, groups)
    .flat();

  data.value.item.promotedOptionGroups = combinedGroups;
  return combinedGroups;
});

const close = () => emits('handle-close');

const getListOptions = () => {
  const item = isQV.value ? props?.item : props?.item ?? false;
  lists.value = store.getOptionLists(item);
};

const mountProductTabs = () => {
  fetch('?section_id=product-tabs')
    .then((res) => res.text())
    .then((data) => {
      let productTabModule = document.createElement('div');
      productTabModule.innerHTML = data;
      productTabModule = productTabModule.firstChild;
      const repositionTabs =
        store.promoReady &&
        window?.Brooklinen?.productTabs?.[0]?.general?.reposition;
      if (productTabModule.childNodes.length > 0) {
        if (document.querySelector('.ProductForm .Upsell') && repositionTabs) {
          document
            .querySelector('.ProductForm .Upsell')
            .insertAdjacentElement('beforebegin', productTabModule);
        } else {
          document.querySelector('.ProductForm').append(productTabModule);
        }

        const productTabs = new ProductTabs();
        productTabs.init();
        addOverlayListeners();
        if (document.querySelector('.accordion .js-size-guide')) {
          document
            .querySelectorAll('.accordion .size-guide__container')
            .forEach((table) => {
              const sizeGuide = new SizeGuide(table);
              sizeGuide.init();
            });
        }
      }
    });
};

const injectGiftCardScript = () => {
  //injects rise ai script after vue component has mounted
  //adding an async or defer attribute to the script does not work, as the scripts will initialize before the ATC computed CSS classes are done

  // Check if the script already exists first and rm if it does.
  const scriptTag = document.getElementById('script#rise-ai');
  if (scriptTag) {
    scriptTag.remove();
  }

  const script = document.createElement('script');
  script.setAttribute('id', 'rise-ai');
  script.setAttribute(
    'src',
    `https://str.rise-ai.com/?shop=${window.Shopify?.shop}`
  );
  document.body.appendChild(script);

  //watches for cart updates in real time, because rise.ai line item properties are not added at the exact same time the gift card product is added to the cart
  initCartWatcher();
};

const initCartWatcher = () => {
  //only runs on gift card PDPs. watches updates to the cart in real time, and fires an event to update the gift card items with line item properties retroactively
  const myCartWatcher = new CartWatcher();
  myCartWatcher.init();
  window.addEventListener('theme:cart-changed', (e) => {
    if (
      e.detail?.added?.length == 1 &&
      e.detail?.added[0].properties?._gift_id
    ) {
      const payload = {
        id: `${e.detail?.added[0]?.variant_id}`,
        properties: e.detail?.added[0]?.properties,
      };
      const event = new CustomEvent('theme:add-to-cart', {
        detail: payload,
      });
      document.dispatchEvent(event);
    }
  });
};

onMounted(() => {
  data.value = props?.item ? props.item : store.$state;
  data.value.item.promotedOptionGroups = promotedOptionGroups.value;

  getListOptions();

  if (!isQV.value) {
    if (!document.querySelector('#shopify-section-product-tabs'))
      mountProductTabs();
    addOverlayListeners();

    if (isGiftCard.value && !document.querySelector('#rise-ai'))
      injectGiftCardScript();
    document.addEventListener('cta:updated', injectGiftCardScript);
  }
});
</script>
